import { useMemo, useRef } from "react";
import { useOutletContext, useParams } from "react-router";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatCompactCurrency } from "@acto/taskpane/field-sales-2/utils/format";
import { AnalyticsEvents, kpiToMetric, useTrack } from "@acto/taskpane/core/analytics";
import {
  rowsToAnalysisRows,
  toSchemaKpi,
  toTimeframe,
  trendToRevenueSnapshot,
} from "@acto/taskpane/core/chat-envelope";
import {
  ANALYSIS_ROW_LIMIT,
  analysisRowsKind,
  trendItemsToAnalysisRowInputs,
} from "@acto/taskpane/field-sales-2/utils/chatScope";
import { useContributeCustomerDetailScope } from "@acto/taskpane/field-sales-2/utils/useContributeCustomerDetailScope";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { trendsToTrendFilter } from "./productQuickFilter";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { AnalysisDrillNav } from "./AnalysisDrillNav";
import { useProductDrill } from "./ProductDrillContext";
import { ProductAnalysisView } from "./ProductAnalysisView";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { useCustomerRevenueTrend } from "@acto/taskpane/field-sales-2/data-access/useCustomerRevenueTrend";
import { useCustomerMarginTrend } from "@acto/taskpane/field-sales-2/data-access/useCustomerMarginTrend";
import { toKpiSingleTrend } from "@acto/taskpane/field-sales-2/data-access/toKpiPairTrend";
import { KpiPairCard, KpiSingleCard } from "@acto/taskpane/field-sales-2/components/primitives/KpiPairCard";
import { useProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { useDelayedViewTrack } from "./useDelayedViewTrack";
import { buildAnalysisFilters } from "./buildAnalysisFilters";
import { useAnalysisListControls } from "./useAnalysisListControls";
import { CustomerOrdersAnalysis } from "./CustomerOrdersAnalysis";
import { Kpi } from "@acto/taskpane/core/domain";
import { useOrdersInRevenueDetailEnabled } from "@acto/taskpane/field-sales-2/data-access/useOrdersInRevenueDetailEnabled";

export function CustomerAnalysis() {
  const { selectedKpi } = useOutletContext<CustomerOutletContext>();
  const isOrdersViewEnabled = useOrdersInRevenueDetailEnabled();

  return isOrdersViewEnabled && selectedKpi === Kpi.ORDERS ? <CustomerOrdersAnalysis /> : <CustomerProductAnalysis />;
}

function CustomerProductAnalysis() {
  const { id } = useParams<{ id: string }>();
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");
  const tKpi = useTranslations("kpi");
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const isKpiCardEnabled = useOrdersInRevenueDetailEnabled();

  const {
    customer,
    isPending: isCustomerPending,
    dateRange,
    isCustomRange,
    selection,
    selectedKpi,
  } = useOutletContext<CustomerOutletContext>();

  // The drill-down happens in place: the trend card stays mounted while the list below
  // descends the product hierarchy.
  const { stack: drillStack } = useProductDrill();
  const currentGroup = drillStack.at(-1);

  const { searchValue, onSearchChange, debouncedSearch, trends, filterSlot } = useAnalysisListControls(
    currentGroup?.id,
  );

  const track = useTrack();
  // Rapid KPI/drill/filter toggles within the delay window collapse into one representative
  // event: the ref carries the latest state so the fire callback isn't stale by the time it runs.
  const trackingStateRef = useRef({ customer, selectedKpi, dateRange, isCustomRange, currentGroup, trends });
  trackingStateRef.current = { customer, selectedKpi, dateRange, isCustomRange, currentGroup, trends };

  useDelayedViewTrack(
    () => {
      const s = trackingStateRef.current;
      if (!s.customer) return;
      track(AnalyticsEvents.CUSTOMER_ANALYSIS_VIEWED, {
        customerId: s.customer.id,
        metric: kpiToMetric(s.selectedKpi),
        filters: buildAnalysisFilters(s),
      });
    },
    `${customer?.id ?? ""}:${selectedKpi}`,
  );

  const groups = useProductGroups({
    clientId: id,
    enabled: !!id,
    parents: currentGroup ? [currentGroup.id] : undefined,
    dateRange,
    searchTerm: debouncedSearch,
    selectedKpi,
    trends,
  });
  const trend = useCustomerRevenueTrend({ clientId: id, kpi: selectedKpi, productGroupId: currentGroup?.id });
  const isMargin = selectedKpi === Kpi.MARGIN;
  const marginTrend = useCustomerMarginTrend(isKpiCardEnabled && isMargin ? id : undefined, currentGroup?.id);

  const analysisFragment = useMemo(
    () => ({
      selected_kpi: toSchemaKpi(selectedKpi),
      ...toTimeframe(selection),
      ...trendToRevenueSnapshot(trend.raw, selection),
      trend_filter: trendsToTrendFilter(trends),
      search_term: debouncedSearch || undefined,
      product_drill_path: drillStack.map((group) => group.name),
      visible_rows_kind: analysisRowsKind(groups.items),
      ...rowsToAnalysisRows(trendItemsToAnalysisRowInputs(groups.items), { limit: ANALYSIS_ROW_LIMIT }),
    }),
    [selectedKpi, selection, trend.raw, trends, debouncedSearch, drillStack, groups.items],
  );
  useContributeCustomerDetailScope("analysis", analysisFragment);

  // Page loader is for the first paint only. The trend card and product list own their own reload
  // states, so a root-level search reloads the list in place rather than blanking the whole page.
  const isPending = isCustomerPending || (!currentGroup && trend.isPending);

  const cardSubtitle = currentGroup?.externalReference ?? customer?.name ?? "";
  const cardTitle = currentGroup?.name ?? tKpi(isMargin ? "marginTab" : "revenueTab");
  const kpiCard = isMargin ? (
    <KpiPairCard subtitle={cardSubtitle} title={cardTitle} trend={marginTrend} />
  ) : (
    <KpiSingleCard
      subtitle={cardSubtitle}
      title={cardTitle}
      trend={toKpiSingleTrend(trend, {
        kpi: tKpi("revenueTab"),
        currentPeriodLabel: tAnalysis("currentPeriod"),
        formatAxis: (value) => formatCompactCurrency(value, currency, locale),
      })}
    />
  );

  return (
    <>
      <ShellSlotContent slot="topBarLeading" id="customerAnalysisDrillNav">
        <AnalysisDrillNav />
      </ShellSlotContent>
      <PageLayout hPadding="card" isLoading={isPending} isEmpty={!customer && !isPending}>
        {!customer ? (
          <>{tAnalysis("customerNotFound", { defaultValue: "Customer not found" })}</>
        ) : (
          <ProductAnalysisView
            {...(isKpiCardEnabled
              ? { kind: "custom" as const, card: kpiCard }
              : {
                  kind: "revenue" as const,
                  rootName: customer.name,
                  rootExternalReference: customer.externalReference ?? undefined,
                  trend,
                })}
            groups={groups}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            filterSlot={filterSlot}
          />
        )}
      </PageLayout>
    </>
  );
}
